import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useAvatar } from "../hooks/useAvatar";
import SkeletonLoader from "./SkeletonLoader";

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  background: #fff;
  border-radius: 8px;
  font-family: "Poppins", sans-serif;
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #222;
  padding-bottom: 0.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  text-align: left;
`;

const Select = styled.select`
  padding: 0.4rem 0.6rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;

  @media (max-width: 768px) {
    padding: 0.25rem 0.4rem;
    font-size: 0.8rem;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 6px;
  font-size: 0.9rem;

  th {
    text-align: left;
    padding: 0.6rem;
    font-weight: 600;
    color: #8d8e8e;
    text-transform: capitalize;

    @media (max-width: 768px) {
      padding: 0.25rem 0.3rem;
      font-size: 0.75rem;
    }
  }

  td {
    padding: 0.6rem;
    vertical-align: middle;
    background: #fff;

    @media (max-width: 768px) {
      padding: 0.25rem 0.3rem;
      font-size: 0.75rem;
    }
  }

  @media (max-width: 768px) {
    border-spacing: 0 3px;
  }
`;

const TableRow = styled.tr`
  border-radius: 10px;
  z-index:999999;
  transition: background 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;

  td:first-child {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }

  td:last-child {
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
  }

  ${({ isCurrent }) => isCurrent ? `
    box-shadow: 0 0 0 1px #ff0102;
    font-weight: 600;
    td {
      background: #fff;
    }
    &:hover td {
      background: #fff;
    }` : `
    &:hover td {
      background: #f9f9f9;
    }
  `}
`;

const Avatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin-right: 8px;

  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
    margin-right: 3px;
  }
`;

const CellContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    gap: 0.2rem;
  }
`;

const HostName = styled.span`
  font-size: 0.9rem;
  color: #222;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 115px;
  width: max-content;

  @media (max-width: 768px) {
    max-width: 70px;
    width: auto;
    white-space: normal;
    overflow: visible;
    text-overflow: initial;
    word-break: break-word;
    font-size: 0.75rem;
  }
`;

const ButtonAction = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: max-content;
`;

const LoaderRow = styled.tr`
  td {
    padding: 1rem;
  }
`;

const ActionsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0px, 1fr));
  gap: 0.6rem;
  width: 100%;
  align-items: center;

  * {
    outline: 0 !important;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    justify-items: start;
  }
`;

const ActionsHeaderCell = styled.th`
  min-width: 75px;
`;

const EmptyCell = styled.td`
  text-align: center;
  color: #777;
`;

export const RankingList = ({
  title = "",
  headers = [],
  data = [],
  selectable = false,
  selectOptions = [],
  selectedValue,
  onSelectChange,
  actions = [],
  getRowKey = (row, i) => i,
  loading = false,
  loaderRows = 5,
  emptyMessage = "No hay datos disponibles",
}) => {
  const { getAvatar } = useAvatar();
  const [users, setUsers] = useState([]);

  useEffect(() => {
    getAvatarArr(data);
  }, [data]);

  const getAvatarArr = async (arr) => {
    const parsed = await Promise.all(
      (arr || [])?.map(async (e) => {
        try {
          e["src"] = await getAvatar(e?.cover || e?.avatar, e.name, false);
          return e;
        } catch (error) {
          // Error al cargar avatar - se usa valor por defecto
          return e;
        }
      })
    );

    setUsers(parsed);
  };

  return (
    <TableWrapper>
      <Header>
        <Title>
          {title || (loading ? <SkeletonLoader active width="150px" /> : "")}
        </Title>

        {selectable && (
          <Select
            value={selectedValue}
            onChange={(e) => onSelectChange?.(e.target.value)}
            disabled={loading}
          >
            {selectOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        )}
      </Header>

      <Table>
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header.key || header}>{header.label || header}</th>
            ))}
            {actions.length > 0 && <ActionsHeaderCell />}
          </tr>
        </thead>

        <tbody>
          {loading ? (
            [...Array(loaderRows)].map((_, i) => (
              <LoaderRow key={i}>
                <td colSpan={headers.length + (actions.length ? 1 : 0)}>
                  <SkeletonLoader active width="100%" height="20px" />
                </td>
              </LoaderRow>
            ))
          ) : users.length > 0 ? (
            users.map((row, i) => (
              <TableRow key={getRowKey(row, i)} isCurrent={row.isCurrent}>
                {headers.map((col) => {
                  const value = row[col.key];
                  return (
                    <td key={col.key}>
                      {col.type === "avatar" ? (
                        <CellContent>
                          <Avatar src={row.src} alt={row.name || "avatar"} />
                          <HostName>{row.name}</HostName>
                        </CellContent>
                      ) : col.render ? (
                        col.render(row, i)
                      ) : (
                        value
                      )}
                    </td>
                  );
                })}

	                {actions.length > 0 && (
	                  <td>
	                    <ActionsContainer>
	                      {actions.map((action, idx) => {
	                        if (action.render) {
	                          return (
	                            <React.Fragment key={action.key || `${getRowKey(row, i)}-action-${idx}`}>
	                              {action.render(row, i)}
	                            </React.Fragment>
	                          );
	                        }
	                        return (
	                          <ButtonAction
	                            key={action.key || `${getRowKey(row, i)}-action-${idx}`}
	                            onClick={() => action.onClick(row)}
	                          >
	                            {action.icon}
                          </ButtonAction>
                        );
                      })}
                    </ActionsContainer>
                  </td>
                )}
              </TableRow>
            ))
          ) : (
            <tr>
              <EmptyCell colSpan={headers.length + (actions.length ? 1 : 0)}>
                {emptyMessage}
              </EmptyCell>
            </tr>
          )}
        </tbody>
      </Table>
    </TableWrapper>
  );
};
